import React, { useEffect, useState } from 'react';
import { FileText, ImageIcon, X } from 'lucide-react';
import { indicatorDict, mockStudents, sumObj } from '../data/mockData';

// 综合素质评分页面
// 页面结构：条件筛选卡片 + 结果卡片（批量按钮 + 学生评分表）+ 右侧评分抽屉
export default function ComprehensiveQualityScoringPage() {
  // ================= 页面状态 =================
  const [students, setStudents] = useState(mockStudents);
  const [drawerConfig, setDrawerConfig] = useState({
    isOpen: false,
    mode: 'view',
    student: null,
  });

  // ================= 事件处理 =================
  const openDrawer = (student, mode) => {
    setDrawerConfig({
      isOpen: true,
      mode,
      student,
    });
  };

  const closeDrawer = () => {
    setDrawerConfig({
      isOpen: false,
      mode: 'view',
      student: null,
    });
  };

  const handleSave = (updatedStudent) => {
    setStudents(prev => prev.map(student => (
      student.id === updatedStudent.id ? updatedStudent : student
    )));
    closeDrawer();
  };

  // ================= 表格计算数据 =================
  const scoringRows = students.map(student => {
    const deMandatory = Number(student.mandatoryScore.de || 0);
    const zhiMandatory = Number(student.mandatoryScore.zhi || 0);
    const tiMandatory = Number(student.mandatoryScore.ti || 0);
    const meiMandatory = Number(student.mandatoryScore.mei || 0);
    const laoMandatory = Number(student.mandatoryScore.lao || 0);
    const deBonus = Number(student.bonusScore.de || 0);
    const zhiBonus = Number(student.bonusScore.zhi || 0);
    const tiBonus = Number(student.bonusScore.ti || 0);
    const meiBonus = Number(student.bonusScore.mei || 0);
    const laoBonus = Number(student.bonusScore.lao || 0);
    const de = student.mandatoryScore.de + sumObj(student.bonusScore.de ? { 1: student.bonusScore.de } : student.bonusScore);
    const zhi = student.mandatoryScore.zhi + sumObj(student.bonusScore.zhi ? { 1: student.bonusScore.zhi } : student.bonusScore);
    const ti = student.mandatoryScore.ti + sumObj(student.bonusScore.ti ? { 1: student.bonusScore.ti } : student.bonusScore);
    const mei = student.mandatoryScore.mei + sumObj(student.bonusScore.mei ? { 1: student.bonusScore.mei } : student.bonusScore);
    const lao = student.mandatoryScore.lao + sumObj(student.bonusScore.lao ? { 1: student.bonusScore.lao } : student.bonusScore);
    const schemeTotal = student.peerScore
      + (student.teacherScore || 0)
      + de
      + zhi
      + ti
      + mei
      + lao
      + student.deduction;

    return {
      ...student,
      deMandatory,
      zhiMandatory,
      tiMandatory,
      meiMandatory,
      laoMandatory,
      deBonus,
      zhiBonus,
      tiBonus,
      meiBonus,
      laoBonus,
      de,
      zhi,
      ti,
      mei,
      lao,
      schemeTotal,
    };
  });

  return (
    <div className="animate-fade-in relative h-full flex flex-col space-y-4">
      {/* 条件筛选卡片：按学院、班级和学生信息查询 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-x-8 gap-y-5">
          <FilterSelect label="学年" />
          <FilterSelect label="学期" />
          <FilterSelect label="批次" />
          <FilterSelect label="学院" />
          <FilterSelect label="班级" />
          <FilterSelect label="是否有方案分" />
          <FilterInput
            label="学号"
            placeholder="请输入学号"
          />
          <FilterInput
            label="姓名"
            placeholder="请输入姓名"
          />
        </div>

        {/* 查询按钮区 */}
        <div className="flex justify-end gap-3 mt-6">
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            重置
          </button>
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            查询
          </button>
        </div>
      </div>

      {/* 结果卡片：综合素质评分列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5 flex-1">
        {/* 表格上方操作按钮区 */}
        <div className="flex space-x-3 mb-4">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors">
            导出
          </button>
        </div>

        {/* 学生评分表格：操作列固定在右侧 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <TableHead>序号</TableHead>
                <TableHead>学号</TableHead>
                <TableHead>姓名</TableHead>
                <TableHead>学院</TableHead>
                <TableHead>班级</TableHead>
                <TableHead>学生互评分</TableHead>
                <TableHead>辅导员评议分</TableHead>
                <TableHead>德育必选项得分</TableHead>
                <TableHead>德育加分项得分</TableHead>
                <TableHead>德育总得分</TableHead>
                <TableHead>智育必选项得分</TableHead>
                <TableHead>智育加分项得分</TableHead>
                <TableHead>智育总得分</TableHead>
                <TableHead>体育必选项得分</TableHead>
                <TableHead>体育加分项得分</TableHead>
                <TableHead>体育总得分</TableHead>
                <TableHead>美育必选项得分</TableHead>
                <TableHead>美育加分项得分</TableHead>
                <TableHead>美育总得分</TableHead>
                <TableHead>劳育必选项得分</TableHead>
                <TableHead>劳育加分项得分</TableHead>
                <TableHead>劳育总得分</TableHead>
                <TableHead>扣分项</TableHead>
                <TableHead>方案分小计</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {scoringRows.map((student, index) => (
                <tr
                  key={student.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <td className="py-3 px-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{student.studentId}</TableCell>
                  <TableCell>{student.name}</TableCell>
                  <TableCell>{student.college}</TableCell>
                  <TableCell>{student.className}</TableCell>
                  <TableCell>{student.peerScore.toFixed(2)}</TableCell>
                  <TableCell>{student.teacherScore || '-'}</TableCell>
                  <TableCell>{student.deMandatory.toFixed(2)}</TableCell>
                  <TableCell>{student.deBonus.toFixed(2)}</TableCell>
                  <TableCell>{student.de.toFixed(2)}</TableCell>
                  <TableCell>{student.zhiMandatory.toFixed(2)}</TableCell>
                  <TableCell>{student.zhiBonus.toFixed(2)}</TableCell>
                  <TableCell>{student.zhi.toFixed(2)}</TableCell>
                  <TableCell>{student.tiMandatory.toFixed(2)}</TableCell>
                  <TableCell>{student.tiBonus.toFixed(2)}</TableCell>
                  <TableCell>{student.ti.toFixed(2)}</TableCell>
                  <TableCell>{student.meiMandatory.toFixed(2)}</TableCell>
                  <TableCell>{student.meiBonus.toFixed(2)}</TableCell>
                  <TableCell>{student.mei.toFixed(2)}</TableCell>
                  <TableCell>{student.laoMandatory.toFixed(2)}</TableCell>
                  <TableCell>{student.laoBonus.toFixed(2)}</TableCell>
                  <TableCell>{student.lao.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className="text-red-500 font-medium">
                      {student.deduction < 0 ? student.deduction : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="bg-gray-50 px-2 py-1 rounded text-gray-800">
                      {student.schemeTotal.toFixed(2)}
                    </span>
                  </TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <div className="flex gap-3">
                      <button
                        onClick={() => openDrawer(student, 'view')}
                        className="text-[#1890ff] hover:underline"
                      >
                        查看
                      </button>
                      <button
                        onClick={() => openDrawer(student, 'edit')}
                        className="text-[#1890ff] hover:underline"
                      >
                        修改
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 右侧评分抽屉：查看或修改单个学生的综合素质评分 */}
      <ScoringDrawer
        isOpen={drawerConfig.isOpen}
        mode={drawerConfig.mode}
        student={drawerConfig.student}
        onClose={closeDrawer}
        onSave={handleSave}
      />
    </div>
  );
}

// 条件筛选：下拉框
function FilterSelect({ label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <select className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-0 outline-none focus:border-[#1890ff] bg-white text-gray-500">
        <option>全部</option>
      </select>
    </div>
  );
}

// 条件筛选：输入框
function FilterInput({ label, placeholder = '' }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <input
        placeholder={placeholder}
        className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-0 outline-none focus:border-[#1890ff]"
      />
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}

// 综合素质评分抽屉
function ScoringDrawer({ isOpen, mode, student, onClose, onSave }) {
  // ================= 抽屉状态 =================
  const [scores, setScores] = useState({});
  const [deduction, setDeduction] = useState(0);
  const [deductionDesc, setDeductionDesc] = useState('');
  const [showTotal, setShowTotal] = useState(false);

  // 学生切换或抽屉打开时，初始化抽屉内表单数据。
  useEffect(() => {
    if (student) {
      setScores(student.mandatoryScore);
      setDeduction(Math.abs(Number(student.deduction || 0)));
      setDeductionDesc(student.deductionDesc || '');
      setShowTotal(mode === 'view');
    }
  }, [student, isOpen, mode]);

  useEffect(() => {
    if (mode === 'edit') {
      setShowTotal(false);
    }
  }, [scores, deduction, mode]);

  if (!isOpen || !student) return null;

  // ================= 计算数据 =================
  const isView = mode === 'view';
  const peer = student.peerScore || 0;
  const teacher = student.teacherScore || 0;
  const mandatoryScoreTotal = sumObj(scores);
  const bonusScoreTotal = sumObj(student.bonusScore);
  const deductionScore = Math.abs(Number(deduction) || 0);
  const schemeScore = peer + teacher + mandatoryScoreTotal + bonusScoreTotal - deductionScore;

  // ================= 事件处理 =================
  const handleSave = () => {
    onSave({
      ...student,
      mandatoryScore: scores,
      deduction: -Math.abs(Number(deduction) || 0),
      deductionDesc,
    });
  };

  const handleScoreChange = (catId, value) => {
    setScores(prev => ({
      ...prev,
      [catId]: Number(value),
    }));
  };

  return (
    <div className={`fixed inset-0 z-50 flex justify-end transition-opacity duration-300 ${isOpen ? 'bg-black/40 opacity-100 visible' : 'bg-transparent opacity-0 invisible pointer-events-none'}`}>
      <div
        className="absolute inset-0"
        onClick={isView ? onClose : undefined}
      />

      <div className={`relative w-[96vw] max-w-[1550px] bg-[#f5f7fa] h-full shadow-[-10px_0_30px_rgba(0,0,0,0.1)] flex flex-col transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* 抽屉标题栏 */}
        <div className="h-[60px] px-6 border-b border-gray-200 flex justify-between items-center bg-white shrink-0 z-20">
          <h2 className="text-[17px] font-bold text-gray-800 flex items-center">
            <div className="w-1 h-4 bg-[#1890ff] rounded-sm mr-2" />
            {isView ? '查看' : '修改'}综合素质评分 - {student.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* 抽屉滚动内容区 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-[14px] mb-4 flex items-center">
              <div className="w-1 h-3 bg-[#1890ff] mr-2" />
              学生基础信息
            </h3>
            <div className="grid grid-cols-4 gap-y-3 text-[13px] text-gray-600 pl-2">
              <div>
                <span className="text-gray-400 w-24 inline-block">姓名：</span>
                <span className="font-medium text-gray-800">{student.name}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">学号：</span>
                <span className="font-medium text-gray-800">{student.studentId}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">学院/专业：</span>
                <span className="text-gray-800">{student.college} - {student.major}</span>
              </div>
              <div>
                <span className="text-gray-400 w-24 inline-block">年级/班级：</span>
                <span className="text-gray-800">{student.grade} - {student.className}</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-[14px] mb-3 flex items-center">
              <div className="w-1 h-3 bg-[#1890ff] mr-2" />
              一、综合测评评价分
            </h3>
            <div className="text-gray-600 text-[13px] pl-2 mb-3">
              (满分: 10分)
            </div>
            <div className="flex items-center gap-16 text-[13px] text-gray-600 pl-2">
              <div>
                <span className="text-gray-500 w-28 inline-block whitespace-nowrap">学生互评分：</span>
                <span className="font-bold text-gray-800">{peer.toFixed(2)}</span> 分
              </div>
              <div>
                <span className="text-gray-500 w-32 inline-block whitespace-nowrap">辅导员评议分：</span>
                <span className="font-bold text-gray-800">{teacher ? teacher.toFixed(2) : '-'}</span> 分
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-[14px] mb-4 flex items-center">
              <div className="w-1 h-3 bg-[#1890ff] mr-2" />
              二、综合测评指标分明细评分 (满分: 90分)
            </h3>

            <IndicatorScoreTable
              student={student}
              isView={isView}
              scores={scores}
              onScoreChange={handleScoreChange}
            />
          </div>

          <NegativeDeductionCard
            isView={isView}
            deduction={deduction}
            setDeduction={setDeduction}
            deductionDesc={deductionDesc}
            setDeductionDesc={setDeductionDesc}
          />

          <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-800 text-[15px] flex items-center">
                综合测评方案分 总计
              </h3>
              <div className="text-[12px] text-gray-400 mt-1">
                非加权合计总分 (满分100) = 综合测评评价分(10) + 综合测评指标分(90) - 负面事项扣分
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {!isView && (
                <button
                  onClick={() => setShowTotal(true)}
                  className="bg-[#1890ff] text-white px-5 py-2 rounded-sm text-[13px] hover:bg-[#096dd9] transition-colors font-medium shadow-sm"
                >
                  计算总分
                </button>
              )}
              <div className="text-right min-w-[100px]">
                {showTotal || isView ? (
                  <span className="text-[24px] font-bold text-[#1890ff]">
                    {schemeScore.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-[16px] font-medium text-gray-400">
                    待计算
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="h-4" />
        </div>

        {/* 抽屉底部操作按钮 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-white z-20 shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-[14px] transition-colors font-medium"
          >
            {isView ? '关闭' : '取消'}
          </button>
          {!isView && (
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#1890ff] text-white rounded hover:bg-[#096dd9] text-[14px] shadow-sm transition-colors font-medium"
            >
              确定并保存评分
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// 综合测评指标分明细表
// 说明：该表将“必选项评分、加分项审核结果、负面扣分”放在同一张表中，便于产品侧按指标继续扩展。
function IndicatorScoreTable({
  student,
  isView,
  scores,
  onScoreChange,
}) {
  let indicatorTotal = 0;

  return (
    <div className="overflow-hidden w-full border border-gray-300 rounded-sm shadow-sm bg-white">
      {/* 使用 table-fixed 强制宽度比例，并用 colgroup 锁死列宽 */}
      <table className="w-full text-left text-[12px] border-collapse table-fixed min-w-full">
        <colgroup>
          <col style={{ width: '8%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '5%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '4%' }} />
          <col style={{ width: '5%' }} />
        </colgroup>
        <thead className="bg-[#f0f2f5] border-b border-gray-300 text-gray-700">
          <tr>
            <th rowSpan={2} className="border-r border-gray-300 px-1 py-2 font-bold text-center align-middle">
              考核大类
            </th>
            <th colSpan={2} className="border-r border-gray-300 px-2 py-2 font-bold text-center">
              必选项
            </th>
            <th colSpan={10} className="border-r border-gray-300 px-2 py-2 font-bold text-center">
              加分项
            </th>
            <th rowSpan={2} className="px-1 py-2 font-bold text-center align-middle text-[#1890ff] leading-tight break-words">
              该项<br />总得分
            </th>
          </tr>
          <tr>
            <th className="border-r border-t border-gray-300 px-3 py-2 font-bold text-center">
              必选项说明
            </th>
            <th className="border-r border-t border-gray-300 px-1 py-2 font-bold text-center leading-tight break-words">
              必选项得分<br />/评分
            </th>
            <th className="border-r border-t border-gray-300 px-2 py-2 font-bold text-center">
              一级指标
            </th>
            <th className="border-r border-t border-gray-300 px-2 py-2 font-bold text-center">
              二级指标
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              需要<br />材料
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              指标<br />得分
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              次数<br />限制
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              分数<br />限制
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              是否<br />参评
            </th>
            <th className="border-r border-t border-gray-300 px-0.5 py-2 font-bold text-center text-[11px] leading-tight break-words">
              参评<br />个数
            </th>
            <th className="border-r border-t border-gray-300 px-2 py-2 font-bold text-center">
              附件或其他内容
            </th>
            <th className="border-r border-t border-gray-300 px-1 py-2 font-bold text-center leading-tight">
              加分项得分<br />汇总
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(indicatorDict).map(([catId, indicator]) => {
            const rows = buildIndicatorRows(indicator);
            const mandatoryScore = Number(scores[catId] ?? 0);
            const bonusScore = Number(student.bonusScore[catId] || 0);
            const categoryTotal = mandatoryScore + bonusScore;
            indicatorTotal += categoryTotal;

            return (
              <React.Fragment key={catId}>
                {rows.map((row, index) => {
                  const isFirstCategoryRow = index === 0;
                  const matchedStudentItem = getPassedBonusData(student, catId, row.secondIndicator);

                  return (
                    <tr
                      key={`${catId}-${row.firstIndicator}-${row.secondIndicator}`}
                      className="border-b border-gray-300 bg-white hover:bg-gray-50"
                    >
                      {isFirstCategoryRow && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 text-center font-bold text-gray-700 bg-[#fafafa]">
                          {indicator.title}
                          <br />
                          <span className="text-gray-500 font-normal text-[11px] leading-5 inline-block px-1">
                            （总分满分{indicator.totalMax}分，必选项满分{indicator.manMax}分，加分项满分{indicator.bonMax}分）
                          </span>
                        </td>
                      )}

                      {isFirstCategoryRow && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 px-3 py-3 align-middle bg-[#fafafa]/40 text-center">
                          <div className="text-gray-600 text-[11px] whitespace-pre-wrap leading-relaxed inline-block text-left">
                            {indicator.manDesc}
                          </div>
                        </td>
                      )}

                      {isFirstCategoryRow && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 text-center px-1 py-2 bg-blue-50/10 align-middle">
                          {isView ? (
                            <span className="font-bold text-gray-800 text-[15px]">
                              {mandatoryScore.toFixed(2)}
                            </span>
                          ) : (
                            <input
                              type="number"
                              min="0"
                              max={indicator.manMax}
                              value={scores[catId] ?? ''}
                              onChange={(event) => onScoreChange(catId, event.target.value)}
                              placeholder="评分"
                              className="border border-blue-400 w-full max-w-[50px] px-1 py-1 text-center rounded outline-none focus:ring-1 focus:ring-[#1890ff] text-[#1890ff] font-bold text-[13px] bg-white placeholder-blue-300"
                            />
                          )}
                        </td>
                      )}

                      {row.isFirstOfGroup && (
                        <td rowSpan={row.groupRowSpan} className="border-r border-gray-300 text-center text-gray-700 font-medium py-2 px-1.5 bg-gray-50/30 break-words">
                          {row.firstIndicator}
                        </td>
                      )}

                      <td className="border-r border-gray-300 px-2 py-2.5 text-gray-800 leading-snug break-words">
                        {row.secondIndicator}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 py-1.5 break-words text-[11px]">
                        {formatLimit(row.materialType)}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 py-1.5 font-bold break-words text-[11px]">
                        {formatLimit(row.score)}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 py-1.5 break-words text-[11px]">
                        {formatLimit(row.timesLimit)}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-600 px-0.5 py-1.5 break-words text-[11px]">
                        {formatLimit(row.scoreLimit)}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-800 px-0.5 py-1.5 break-words text-[11px]">
                        {matchedStudentItem ? matchedStudentItem.isParticipate : '-'}
                      </td>
                      <td className="border-r border-gray-300 text-center text-gray-800 px-0.5 py-1.5 break-words text-[11px]">
                        {matchedStudentItem ? matchedStudentItem.count : '-'}
                      </td>
                      <td className="border-r border-gray-300 px-2 py-1.5 align-middle break-words">
                        {matchedStudentItem && matchedStudentItem.attachments ? (
                          <div className="flex flex-col space-y-1.5 my-1">
                            {matchedStudentItem.attachments.map((attachment, attachmentIndex) => {
                              const isPdf = attachment.endsWith('.pdf');
                              const isImage = attachment.endsWith('.png') || attachment.endsWith('.jpg');

                              return (
                                <span key={attachmentIndex} className="text-[#1890ff] hover:underline cursor-pointer w-full block text-[11px] flex items-start" title={attachment}>
                                  {isPdf ? (
                                    <FileText size={11} className="mr-1 shrink-0 text-red-400 mt-0.5" />
                                  ) : isImage ? (
                                    <ImageIcon size={11} className="mr-1 shrink-0 text-green-500 mt-0.5" />
                                  ) : (
                                    <FileText size={11} className="mr-1 shrink-0 text-gray-400 mt-0.5" />
                                  )}
                                  <span className="break-words leading-tight">
                                    {attachment}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-center block w-full">
                            -
                          </span>
                        )}
                      </td>

                      {isFirstCategoryRow && (
                        <td rowSpan={rows.length} className="border-r border-gray-300 text-center font-bold text-gray-800 align-middle bg-[#fcfcfc] text-[14px]">
                          {bonusScore.toFixed(2)}
                        </td>
                      )}

                      {isFirstCategoryRow && (
                        <td rowSpan={rows.length} className="text-center font-bold text-[#1890ff] align-middle bg-blue-50/10 text-[15px]">
                          {categoryTotal.toFixed(2)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}

          <tr className="bg-[#f0f2f5] border-t border-gray-300">
            <td className="border-r border-gray-300 px-4 py-3 font-bold text-gray-800 text-right tracking-widest" colSpan={13}>
              综合测评指标分 小计
            </td>
            <td className="px-2 py-3 text-center font-bold text-[#1890ff] text-[16px]">
              {indicatorTotal.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// 负面事项扣分卡片：从指标明细表中独立出来，避免和正向指标混在一起。
function NegativeDeductionCard({
  isView,
  deduction,
  setDeduction,
  deductionDesc,
  setDeductionDesc,
}) {
  const deductionValue = Math.abs(Number(deduction) || 0);

  return (
    <div className="bg-white p-5 rounded-sm border border-gray-200 shadow-sm">
      <h3 className="font-bold text-gray-800 text-[14px] mb-4 flex items-center">
        <div className="w-1 h-3 bg-red-500 mr-2" />
        负面事项扣分
      </h3>

      <div className="text-gray-500 text-[12px] mb-4 space-y-1 bg-gray-50 p-3 rounded border border-gray-100">
        <div>
          <strong className="text-gray-600">扣分说明：</strong>
        </div>
        <div>
          1. 违反国家法律法规，按照国家相关规定进行处理，当学期综合素质测评成绩记为 0分。
        </div>
        <div>
          2. 违反校纪校规等日常管理规定，视情节轻重给予 10-50分 的扣分处理。在此之外允许辅导员填写扣分描述/依据（非必选）。
        </div>
      </div>

      <div className="grid grid-cols-[160px_1fr] gap-5 text-[13px] text-gray-700">
        <div>
          <div className="text-gray-500 mb-2">
            扣分分值：
          </div>
          {isView ? (
            <div className="font-bold text-red-500 text-[18px]">
              {deductionValue !== 0 ? deductionValue.toFixed(2) : '-'}
            </div>
          ) : (
            <input
              type="number"
              min="0"
              value={deductionValue || ''}
              onChange={(event) => setDeduction(Math.abs(event.target.value))}
              placeholder="正值"
              className="border border-red-300 w-20 px-2 py-1.5 text-center rounded outline-none focus:ring-1 focus:ring-red-500 text-red-600 font-bold text-[13px] bg-white placeholder-red-400"
            />
          )}
        </div>

        <div>
          <div className="text-gray-500 mb-2">
            扣分描述/依据：
          </div>
          {isView ? (
            <div className="text-gray-800 bg-[#fafafa] border border-gray-200 p-2 rounded min-h-[50px] whitespace-pre-wrap">
              {deductionDesc || '无'}
            </div>
          ) : (
            <textarea
              value={deductionDesc ?? ''}
              onChange={(event) => setDeductionDesc(event.target.value)}
              placeholder="非必填，允许辅导员填写补充记录..."
              className="w-full bg-white border border-gray-300 rounded p-2.5 text-[12px] outline-none focus:border-[#1890ff] focus:ring-1 focus:ring-[#1890ff] resize-y min-h-[70px]"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// 将指标配置转换为明细表行，并保留一级指标合并单元格需要的行数。
function buildIndicatorRows(indicator) {
  return indicator.bonusCats.flatMap((bonusCategory) => (
    bonusCategory.items.map((item, index) => ({
      firstIndicator: bonusCategory.name,
      secondIndicator: item.name,
      materialType: item.needProof,
      score: item.itemScore,
      timesLimit: item.countLimit,
      scoreLimit: item.scoreLimit,
      isFirstOfGroup: index === 0,
      groupRowSpan: bonusCategory.items.length,
    }))
  ));
}

// 当前 mock 里通过的加分项按分类保存，这里负责将其映射到指标表中的参评记录。
function getPassedBonusData(student, catId, secondIndicator) {
  const passedItems = student.bonusDetails?.[catId] || [];
  const matchedItem = passedItems.find(item => secondIndicator.includes(item.name) || item.name.includes(secondIndicator));

  if (!matchedItem) {
    return null;
  }

  return {
    isParticipate: '是',
    count: 1,
    attachments: matchedItem.attachments || [`${matchedItem.name}.pdf`],
  };
}

function formatLimit(value) {
  return value === undefined || value === null || value === '' || value === '无'
    ? '-'
    : value;
}
